/**
 * Play-time recovery when localStorage is empty but the Connector's auth.json
 * still holds valid credentials — avoids treating a recoverable desync as fatal.
 */
import { refreshAuthJsonSession } from './authInitWatchdog';
import { isValidRefreshToken, tokenFpSync } from './authTokens';
import {
  classifyAuthJsonRestoreFailure,
  isDeadRefreshTokenResponse,
  logAuthJsonRecoverySuccess,
  logRefreshFailed,
  logSignoutDecision,
  parseRefreshErrorDetails,
  readLocalStorageRefreshFp,
} from './authDiagnostics';
import { noteConnectorStoredSession } from './authDeclaw';
import { noteAuthJsonRestoreFatalReason } from './authDeclawDeadFamily';
import { maybeLatchFromRefreshRejection } from './authTerminalLatch';
import { isRefreshTokenAlreadyUsedError } from '../services/api';
import { isPoisonedRefreshToken } from './sessionDead';

export type AuthJsonRestoreResult =
  | {
      ok: true;
      accessToken: string;
      refreshToken: string;
      user?: { id?: string; email?: string };
      via: string;
    }
  | { ok: false; fatal: boolean; reason: string };

const MAX_ATTEMPTS = 4;
const BACKOFF_MS = [0, 300, 800, 1800];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

type AuthJsonCreds = {
  refresh_token: string;
  user_id?: string;
  email?: string;
};

function adoptTokensToLocalStorage(
  accessToken: string,
  refreshToken: string,
  creds?: AuthJsonCreds | null,
): void {
  const stored = localStorage.getItem('maestro_auth');
  const current: Record<string, unknown> = stored ? JSON.parse(stored) : {};
  localStorage.setItem('maestro_auth', JSON.stringify({
    ...current,
    accessToken,
    refreshToken,
    user: current.user || (creds ? { id: creds.user_id, email: creds.email } : undefined),
  }));
  window.dispatchEvent(new CustomEvent('maestro:token-refreshed', {
    detail: { accessToken, refreshToken },
  }));
}

export async function readValidAuthJsonCreds(): Promise<AuthJsonCreds | null> {
  if (typeof window === 'undefined' || !('__TAURI__' in window)) return null;
  const { readAuthJson } = await import('./connector');
  const creds = await readAuthJson();
  if (!isValidRefreshToken(creds?.refresh_token)) {
    noteConnectorStoredSession(false);
    return null;
  }
  noteConnectorStoredSession(true);
  return creds as AuthJsonCreds;
}

export async function connectorHasValidAuthJson(): Promise<boolean> {
  try {
    return !!(await readValidAuthJsonCreds());
  } catch {
    return false;
  }
}

async function tryEnsureFreshRestore(): Promise<Extract<AuthJsonRestoreResult, { ok: true }> | null> {
  const { tryEnsureFreshToken, readAuthJson, isConnectorAvailable } = await import('./connector');
  const { shouldSuppressAutomaticRefresh } = await import('./authTerminalLatch');
  if (!isConnectorAvailable() || shouldSuppressAutomaticRefresh()) return null;
  const outcome = await tryEnsureFreshToken();
  if (outcome.kind !== 'ok') return null;

  const creds = await readAuthJson().catch(() => null);
  const refreshToken = outcome.result.refresh_token || creds?.refresh_token || '';
  if (!isValidRefreshToken(refreshToken)) return null;

  adoptTokensToLocalStorage(outcome.result.access_token, refreshToken, creds);
  return {
    ok: true,
    accessToken: outcome.result.access_token,
    refreshToken,
    user: creds ? { id: creds.user_id, email: creds.email } : undefined,
    via: 'ensure_fresh_token',
  };
}

async function rereadAuthJsonRefreshToken(currentFp: string): Promise<string | null> {
  const creds = await readValidAuthJsonCreds();
  if (!creds) return null;
  const newFp = tokenFpSync(creds.refresh_token);
  if (newFp === currentFp || newFp === 'none') return null;
  return creds.refresh_token;
}

/**
 * Recover a web session from Connector auth.json — connector-owned refresh first,
 * then direct HTTP with re-read/retry before declaring fatal.
 */
export async function recoverSessionFromAuthJson(source = 'auth.json'): Promise<AuthJsonRestoreResult> {
  const creds = await readValidAuthJsonCreds();
  if (!creds) {
    noteAuthJsonRestoreFatalReason('invalid_token');
    return { ok: false, fatal: true, reason: 'invalid_token' };
  }
  if (isPoisonedRefreshToken(creds.refresh_token)) {
    noteAuthJsonRestoreFatalReason('poisoned_refresh');
    return { ok: false, fatal: true, reason: 'poisoned_refresh' };
  }

  const oldFp = tokenFpSync(creds.refresh_token);
  console.info(
    `[Auth:lifecycle] restoreFromAuthJson auth.json_fp=${oldFp} localStorage_fp=${readLocalStorageRefreshFp()}`,
  );

  const viaConnector = await tryEnsureFreshRestore();
  if (viaConnector) {
    logAuthJsonRecoverySuccess(viaConnector.via, oldFp, tokenFpSync(viaConnector.refreshToken), source);
    return viaConnector;
  }

  let refreshToken = creds.refresh_token;

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    if (attempt > 0) {
      await sleep(BACKOFF_MS[attempt] ?? 1800);
    }

    const response = await refreshAuthJsonSession(refreshToken);
    const data = await response.json().catch(() => null);
    const newAccess = data?.session?.access_token || data?.access_token;
    const newRefresh = data?.session?.refresh_token || data?.refresh_token || refreshToken;

    if (response.ok && newAccess && isValidRefreshToken(newRefresh)) {
      adoptTokensToLocalStorage(newAccess, newRefresh, creds);
      const via = attempt === 0 ? 'direct_refresh' : `direct_refresh_retry_${attempt}`;
      logAuthJsonRecoverySuccess(via, oldFp, tokenFpSync(newRefresh), source);
      return {
        ok: true,
        accessToken: newAccess,
        refreshToken: newRefresh,
        user: data.user || data.session?.user || { id: creds.user_id, email: creds.email },
        via,
      };
    }

    if ((response.status === 401 || response.status === 403) && isRefreshTokenAlreadyUsedError(data)) {
      const rotated = await rereadAuthJsonRefreshToken(tokenFpSync(refreshToken));
      if (rotated) {
        refreshToken = rotated;
        continue;
      }
      const healed = await tryEnsureFreshRestore();
      if (healed) {
        logAuthJsonRecoverySuccess(healed.via, oldFp, tokenFpSync(healed.refreshToken), source);
        return healed;
      }
      const stillHas = await connectorHasValidAuthJson();
      noteAuthJsonRestoreFatalReason('already_used');
      return { ok: false, fatal: !stillHas, reason: 'already_used' };
    }

    try { logRefreshFailed(response.status, data, `authJsonRestore:${source}`); } catch { /* diagnostic */ }

    if (isDeadRefreshTokenResponse(response.status, data)) {
      try {
        const { errorCode } = parseRefreshErrorDetails(data);
        logSignoutDecision(errorCode, 'fatal', response.status);
      } catch { /* diagnostic */ }
      maybeLatchFromRefreshRejection(response.status, data);
      noteAuthJsonRestoreFatalReason('refresh_rejected');
      return { ok: false, fatal: true, reason: 'refresh_rejected' };
    }

    const healed = await tryEnsureFreshRestore();
    if (healed) {
      logAuthJsonRecoverySuccess(healed.via, oldFp, tokenFpSync(healed.refreshToken), source);
      return healed;
    }

    const rotated = await rereadAuthJsonRefreshToken(tokenFpSync(refreshToken));
    if (rotated) {
      refreshToken = rotated;
      continue;
    }

    const stillHas = await connectorHasValidAuthJson();
    const classified = classifyAuthJsonRestoreFailure(response.status, data, stillHas);

    if (classified === 'fatal') {
      try {
        const { errorCode } = parseRefreshErrorDetails(data);
        logSignoutDecision(errorCode, classified, response.status);
      } catch { /* diagnostic */ }
      maybeLatchFromRefreshRejection(response.status, data);
      noteAuthJsonRestoreFatalReason('refresh_rejected');
      return { ok: false, fatal: true, reason: 'refresh_rejected' };
    }

    if (attempt + 1 >= MAX_ATTEMPTS) {
      return {
        ok: false,
        fatal: !stillHas,
        reason: stillHas ? 'retry_exhausted_recoverable' : 'retry_exhausted',
      };
    }
  }

  const stillHas = await connectorHasValidAuthJson();
  return { ok: false, fatal: !stillHas, reason: 'exhausted' };
}
